import { readReferralCode } from './a-referral-code-is-typed-at-onboarding';

/**
 * A referral code that arrives by a link fills the field of the onboarding by
 * itself (Asana 1219743222744698, 22 October 2026).
 *
 * THE LINK READ HERE IS `/open/parrainage/<code>`, and `/parrainage/<code>`
 * without the prefix. The second is the page planned on smatchy.app (Asana
 * 1219733877878952): the day it exists and the association file lists it, the
 * app reads it without a new release. Until then only the `/open/` form reaches
 * the app on iOS, and that form is a 404 on smatchy.app for somebody without
 * the app, which is why the invite of « Mes amis » still shares the bare site
 * with the code in its text.
 *
 * A `?parrainage=<code>` on any other link is read too, so a link to an
 * activity can carry the code of whoever shared it.
 *
 * KEPT ON THE DEVICE, not in memory. Between the link and the field there is
 * the whole sign-up, e-mail confirmation included, and the app may be closed
 * and opened again in the middle.
 *
 * Pure on purpose: no Angular.
 */

const OWN_PATH = /^\/(?:open\/)?parrainage\/([^/?#]+)\/?$/i;

export interface ALinkCarries {
  code: string;
  /** True when the link is the referral link itself, which has no screen of its own. */
  ownLink: boolean;
}

export const whatALinkCarries = (link: string | URL): ALinkCarries | undefined => {
  let url: URL;
  try {
    url = typeof link === 'string' ? new URL(link) : link;
  } catch {
    return undefined;
  }
  const own = url.pathname.match(OWN_PATH);
  if (own) {
    const code = readReferralCode(decodeURIComponent(own[1]));
    return code ? { code, ownLink: true } : undefined;
  }
  const code = readReferralCode(url.searchParams.get('parrainage'));
  return code ? { code, ownLink: false } : undefined;
};

export const REFERRAL_CODE_FROM_A_LINK_KEY = 'referralCodeFromALink';

type Writable = Pick<Storage, 'setItem' | 'removeItem'>;
type Readable = Pick<Storage, 'getItem'>;

/** A storage that throws (private mode, a full disk) never stops a link from opening. */
export const keepTheCodeOfALink = (code: string, storage: Writable = localStorage): void => {
  try {
    storage.setItem(REFERRAL_CODE_FROM_A_LINK_KEY, code);
  } catch {
    // Nothing to do: the person can still type it.
  }
};

export const theCodeOfALink = (storage: Readable = localStorage): string | undefined => {
  try {
    return readReferralCode(storage.getItem(REFERRAL_CODE_FROM_A_LINK_KEY));
  } catch {
    return undefined;
  }
};

/** Forgotten once claimed, so a second account on the same phone is not prefilled. */
export const forgetTheCodeOfALink = (storage: Writable = localStorage): void => {
  try {
    storage.removeItem(REFERRAL_CODE_FROM_A_LINK_KEY);
  } catch {
    // Nothing to do.
  }
};
